import { createRoute } from '@tanstack/react-router';

import {
  blastByNameQueryOptions,
  blastLimitsQueryOptions,
  environmentQueryOptions,
  hardnessDomainByBlastQueryOptions,
  infrastructureQueryOptions,
  optimiserByBlastQueryOptions,
  unsurveyedQueryOptions,
} from '~api/queries';
import { queryClient } from '~config';
import { BlastOptimisationViewer } from '~features/blast-optimisation-viewer';
import { BlastOptimisations } from '~features/blast-optimisations';
import { useTableStore } from '~store';
import { blastsRootRoute } from './blasts';

/**
 * Shared loader logic for blast optimisation routes.
 * Ensures blast data is available and pre-fetches related queries.
 */
const loadBlastOptimisationsData = async (blastName: string) => {
  // Load the blast data based on the blastName parameter
  // Note: this query is cached by react-query when navigating from BlastList, so this operation guarantees data availability when directly accessing the URL.
  const blast = await queryClient.ensureQueryData(blastByNameQueryOptions(blastName));
  // Pre-fetch optimiser data before navigating to child routes, without blocking the navigation
  queryClient.prefetchQuery(optimiserByBlastQueryOptions(blastName));
  // Pre-fetch hardness domain data as well
  queryClient.prefetchQuery(hardnessDomainByBlastQueryOptions(blastName));
  // Pre-fetch associated sensitive sites data
  queryClient.prefetchQuery(blastLimitsQueryOptions(blastName));
  // Pre-fetch associated infrastructure sites data (if applicable)
  queryClient.prefetchQuery(infrastructureQueryOptions({ blast_name: blastName }));
  // Pre-fetch unsurveyed sites data
  queryClient.prefetchQuery(unsurveyedQueryOptions({ blast_name: blastName }));
  // Pre-fetch environment sites data
  queryClient.prefetchQuery(environmentQueryOptions({ blast_name: blastName }));

  return { blast, blastName };
};

/**
 * Shared cleanup logic when leaving blast optimisation routes.
 * Clears global UI state (selected blast, table sorting/filtering).
 *
 * Note: critical render data (e.g., blast polygon) is read from the TanStack
 * Query cache in consuming components, so this cleanup does not cause rendering
 * gaps during sibling route transitions.
 */
const cleanupBlastOptimisationState = () => {
  useTableStore.getState().resetSorting();
  useTableStore.getState().resetFiltering();
};

/**
 * Route for `/blasts/$blastName/optimisations` — displays the list of optimisations for a specific blast.
 */
const blastOptimisationsRoute = createRoute({
  getParentRoute: () => blastsRootRoute,
  path: '$blastName/optimisations',
  component: BlastOptimisations,
  loader: async ({ params }) => {
    const { blast, blastName } = await loadBlastOptimisationsData(params.blastName);

    return {
      crumbs: [
        { label: params.blastName, href: '/blasts' },
        { label: 'Optimisations', href: `/blasts/${params.blastName}/optimisations` },
      ],
      blast,
      blastName,
    };
  },
  onLeave: cleanupBlastOptimisationState,
});

/**
 * Route for `/blasts/$blastName/optimisations/$optimiserId` — displays the pattern design viewer for a specific blast optimisation.
 */
const blastOptimiserViewerRoute = createRoute({
  getParentRoute: () => blastsRootRoute,
  path: '$blastName/optimisations/$optimiserId',
  component: BlastOptimisationViewer,
  loader: async ({ params }) => {
    const { blast, blastName } = await loadBlastOptimisationsData(params.blastName);

    return {
      crumbs: [
        { label: params.blastName, href: '/blasts' },
        { label: 'Optimisations', href: `/blasts/${params.blastName}/optimisations` },
        { label: params.optimiserId, href: `/blasts/${params.blastName}/optimisations/${params.optimiserId}` },
      ],
      blast,
      blastName,
    };
  },
  onLeave: cleanupBlastOptimisationState,
});

export { blastOptimisationsRoute, blastOptimiserViewerRoute };
